import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GridCellKind, type EditableGridCell } from '@glideapps/glide-data-grid';
import type { ProjectAFundSummary } from '@nemo/api';
import { orderedColumns, sortRows, type GridColumnDef, type Sort } from '../grid/columns.js';
import { DataGrid } from '../grid/DataGrid.js';
import { DensityToggle } from '../grid/DensityToggle.js';
import { readText, text } from '../grid/fundColumns.js';
import { useGridPreferences } from '../grid/useGridPreferences.js';
import { useTRPC } from '../trpc.js';
import { useToast } from '../ui/Toast.js';

const PREFERENCES_KEY = 'nemo.grid.projectAFunds.v1';

/** One field of a Project A fund, as a finished cell edit asks for it. */
type FundEdit =
  { code: string } | { name: string } | { vintageYear: number | null } | { active: boolean };

interface ProjectAFundColumn extends GridColumnDef<ProjectAFundSummary> {
  /** The edit a finished cell asks for, or null where the value cannot be held. */
  readonly commit?: (fund: ProjectAFundSummary, cell: EditableGridCell) => FundEdit | null;
}

const COLUMNS: readonly ProjectAFundColumn[] = [
  {
    id: 'code',
    title: 'Code',
    width: 120,
    editsOnEnter: true,
    cell: (fund) => ({ ...text(fund.code, true), themeOverride: { baseFontStyle: '600 13px' } }),
    text: (fund) => fund.code,
    sortKey: (fund) => fund.code,
    commit: (_fund, cell) => {
      const code = readText(cell)?.trim();
      return code === undefined || code === '' ? null : { code };
    },
  },
  {
    id: 'name',
    title: 'Name',
    width: 280,
    editsOnEnter: true,
    // A fund named only by its code reads as unnamed rather than twice.
    cell: (fund) => text(fund.name === fund.code ? '' : fund.name, true),
    text: (fund) => fund.name,
    sortKey: (fund) => fund.name.toLowerCase(),
    // Blank is the code again, which is what a fund nobody named is called.
    commit: (fund, cell) => {
      const name = readText(cell)?.trim();
      return name === undefined ? null : { name: name === '' ? fund.code : name };
    },
  },
  {
    id: 'vintage',
    title: 'Vintage',
    width: 90,
    editsOnEnter: true,
    cell: (fund) => ({
      kind: GridCellKind.Number,
      data: fund.vintageYear ?? undefined,
      displayData: fund.vintageYear === null ? '' : String(fund.vintageYear),
      allowOverlay: true,
      contentAlign: 'right',
    }),
    text: (fund) => (fund.vintageYear === null ? '' : String(fund.vintageYear)),
    sortKey: (fund) => fund.vintageYear,
    commit: (_fund, cell) => {
      if (cell.kind !== GridCellKind.Number) return null;
      return { vintageYear: cell.data === undefined ? null : Math.round(cell.data) };
    },
  },
  {
    id: 'active',
    title: 'Active',
    width: 80,
    cell: (fund) => ({ kind: GridCellKind.Boolean, data: fund.active, allowOverlay: false }),
    text: (fund) => (fund.active ? 'Active' : 'Closed'),
    sortKey: (fund) => (fund.active ? 0 : 1),
    commit: (_fund, cell) =>
      cell.kind === GridCellKind.Boolean && typeof cell.data === 'boolean'
        ? { active: cell.data }
        : null,
  },
  {
    id: 'companiesHeld',
    title: 'Companies held',
    width: 140,
    cell: (fund) => ({ ...text(String(fund.companiesHeld), false), contentAlign: 'right' }),
    text: (fund) => String(fund.companiesHeld),
    sortKey: (fund) => fund.companiesHeld,
  },
];

/**
 * Project A's own funds: the codes a portfolio company is held under, and
 * what each stands for. Edited in the cell; a changed code follows into every
 * company held under it.
 */
export function ProjectAFunds(): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const funds = useQuery(trpc.projectAFunds.list.queryOptions());
  const [sort, setSort] = useState<Sort | null>(null);
  const preferences = useGridPreferences(PREFERENCES_KEY);
  const columns = useMemo(() => orderedColumns(COLUMNS, preferences), [preferences]);
  const rows = useMemo(
    () => sortRows(funds.data ?? [], columns, sort, undefined),
    [funds.data, columns, sort],
  );

  const upsert = useMutation(
    trpc.projectAFunds.upsert.mutationOptions({
      onSuccess: (fund) => {
        void queryClient.invalidateQueries({ queryKey: trpc.projectAFunds.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.portfolio.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.coInvestments.options.queryKey() });
        toast(`${fund?.code ?? 'The fund'} saved.`, { tone: 'info' });
      },
      onError: (error: { message: string }) => toast(error.message),
    }),
  );

  function onCellEdited(
    fund: ProjectAFundSummary,
    column: ProjectAFundColumn,
    value: EditableGridCell,
  ): void {
    if (column.commit === undefined) return;
    const edit = column.commit(fund, value);
    if (edit === null) {
      toast(`That is not a value ${column.title} can hold.`);
      return;
    }
    upsert.mutate({ id: fund.id, code: fund.code, ...edit });
  }

  if (funds.isPending) return <p className="subtle loading">Loading the funds…</p>;
  if (funds.isError) return <p className="subtle loading">{funds.error.message}</p>;

  return (
    <>
      <div className="toolbar">
        <strong className="count">{funds.data.length} Project A funds</strong>
        <span className="subtle">
          Double-click a cell to change it; tick Active to close a fund.
        </span>
        <span className="spacer" />
        <DensityToggle density={preferences.density} onChange={preferences.setDensity} />
        <button
          className="button primary"
          type="button"
          disabled={upsert.isPending}
          onClick={() => {
            const code = window.prompt('New Project A fund code, like PAV VI');
            if (code !== null && code.trim() !== '') upsert.mutate({ code: code.trim() });
          }}
        >
          <Plus aria-hidden="true" />
          Add fund
        </button>
      </div>
      <DataGrid
        rows={rows}
        columns={columns}
        context={undefined}
        rowId={(fund) => fund.id}
        preferences={preferences}
        sort={{ value: sort, onChange: setSort }}
        onCellEdited={onCellEdited}
        freezeColumns={1}
        empty={<span className="subtle">No Project A funds yet.</span>}
      />
    </>
  );
}
